import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { OverlayProvider } from 'react-aria';
import clsx from 'clsx';
import Image from 'next/image';
import { useOverlayTriggerState } from 'react-stately';
import { useDebounce } from 'usehooks-ts';
import {
  Button,
  Icon,
  IconType,
  Input,
  Label,
  manualEntryOption,
  MonitoredBy,
  Popup,
  SelectListItem,
  SelectRCWallet,
} from '@/components/common';
import { ItemType } from '@/components/common/form/SelectRC/SelectRCOption';
import { BuyCryptoStoreInterface, useBuyCrypto } from '@/store/buyCrypto';
import { allowOnlyNumber, IsERC20Network, isEVMNetwork, truncateString } from '@/utils/functions';
import { useVerifyWallet } from '@/api/queryHooks/useBuyCryptoController';
import { useCheckStatus } from '@/api/queryHooks/useKycController';
import { CryptoCurrency, KycStatus } from '@/api/common.types';
import { ProviderLabel } from '@web3-onboard/injected-wallets';
import {
  ConnectWallet,
  IConnectWalletRef,
  OnConnectWallet,
} from '@/views/BuyCrypto/components/ConnectWallet/ConnectWallet';
import { shallow } from 'zustand/shallow';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useRole } from '@/hooks/useRole';
import { Roles } from '@/constants/roles';
import { useGetDepositWallet } from '@/api';
import { Select } from '@/components/common/form/Select';
import { NotAllowedWarning } from '@/components/common/popups/NotAllowed';
import { showNotification } from '@/utils/showNotification';
import { useGetCryptoNetworkData } from '@/hooks/useGetCryptoNetworkData';
import { MemoWarning } from '@/components/common/popups/MemoWarning';
import { useChainsData } from '@/store/chainsData';
import { Verification } from '../components';
import { StepProps } from './EnterAmount';
import styles from './Steps.module.css';

const EXCEPTION_ERROR_MESSAGE = 'Read timed out; nested exception is java.net.SocketTimeoutException: Read timed out';

function buildCoinsData() {
  const coins = [
    { symbol: CryptoCurrency.BTC, name: 'bitcoin' },
    { symbol: CryptoCurrency.ETH, name: 'ethereum' },
    { symbol: CryptoCurrency.USDT, name: 'usdt' },
    { symbol: CryptoCurrency.USDC, name: 'usdc' },
  ];
  return coins.map((coin) => {
    return {
      value: coin.symbol,
      icon: coin.name,
    };
  });
}

const coinsData = buildCoinsData();

const METAMASK_WALLET_OBJECT = {
  label: ProviderLabel.MetaMask,
  value: 'metaMask',
  icon: <Image src="/assets/MetamaskWallet.svg" width={18} height={18} alt="MetaMask" priority loading="eager" />,
};
let WALLETS: ItemType[] = [
  {
    label: ProviderLabel.Trust,
    value: 'trust',
    icon: <Image src="/assets/TrustWalletLogo.svg" width={18} height={18} alt="MetaMask" priority loading="eager" />,
  },
  {
    label: ProviderLabel.Coinbase,
    value: 'coinbase',
    icon: <Image src="/assets/CoinbaseLogo.svg" width={18} height={18} alt="Coinbase" priority loading="eager" />,
  },
  {
    label: 'Trezor',
    value: 'trezor',
    icon: <Image src="/assets/TrezorWallet.svg" width={18} height={18} alt="Trezor" priority loading="eager" />,
  },
  {
    label: 'Ledger',
    value: 'ledger',
    icon: <Image src="/assets/LedgerWalletLogo.svg" width={18} height={18} alt="Ledger" priority loading="eager" />,
  },
];

type WalletStatus = 'failedLocal' | 'verifying' | 'failed' | 'verified' | null;

const renderInputLabel = (walletStatus: WalletStatus, isDesktop: boolean, isSelectLabel?: boolean) => {
  switch (walletStatus) {
    case 'verifying':
      return (
        <Label
          color="blue"
          icon="loader"
          size={isDesktop ? 'medium' : 'small'}
          className={clsx(styles.inputLabel, isSelectLabel && styles.InputSelectLabel)}
        >
          Verifying
        </Label>
      );
    case 'failed':
      return (
        <Label
          color="red"
          icon="close"
          size={isDesktop ? 'medium' : 'small'}
          className={clsx(styles.inputLabel, isSelectLabel && styles.InputSelectLabel)}
        >
          Failed
        </Label>
      );
    case 'failedLocal':
      return (
        <Label
          color="gray"
          size={isDesktop ? 'medium' : 'small'}
          className={clsx(styles.inputLabel, isSelectLabel && styles.InputSelectLabel)}
        >
          Needs attention
        </Label>
      );
    case 'verified':
      return (
        <Label
          color="green"
          icon="check"
          size={isDesktop ? 'medium' : 'small'}
          className={clsx(styles.inputLabel, isSelectLabel && styles.InputSelectLabel)}
        >
          Verified
        </Label>
      );
    default:
      return null;
  }
};

const renderWalletNetworkMessage = (walletStatus: WalletStatus, network: CryptoCurrency) => {
  switch (walletStatus) {
    case 'verified':
      return (
        <div className={styles.walletNetworkInfo}>
          <Icon name="check" className={styles.walletNetworkInfoIcon} />
          Your address is compatible with the {network} network(s).
        </div>
      );
    case 'failedLocal':
    case 'failed':
      return (
        <div className={styles.walletNetworkWarning}>
          <Icon name="warning" className={styles.walletNetworkWarningIcon} />
          <div>
            <div style={{ fontWeight: '600' }}>Your Address not compatible with {network} network(s).</div> To continue
            choose another Wallet Address compatible with the selected network.
          </div>
        </div>
      );
    default:
      return '';
  }
};

export const WalletAddress = ({ step, setStep }: StepProps) => {
  const state = useOverlayTriggerState({});
  const { isDesktop } = useMediaQuery();
  const buyState = useBuyCrypto((state) => state, shallow);
  const [walletStatus, setWalletStatus] = useState<WalletStatus>(null);
  const [connectedWallet, setConnectedWallet] = useState<ItemType | undefined>(undefined);
  const connectWalletRef = useRef<IConnectWalletRef>(null);
  const [memoFieldError, setMemoFieldError] = useState<string>('');
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
  const [chains] = useChainsData((state) => [state.chainsData, state.setData], shallow);

  const role = useRole();
  // const allowedNetwork = process.env.NEXT_PUBLIC_ALLOWED_CHAIN_ID;
  const { data: depositWalletData, refetch: refetchDepositWallets } = useGetDepositWallet([
    { cryptoCurrency: buyState.get, cryptoNetwork: buyState.network },
    { enabled: true },
  ]);

  const hasDepositWallets = depositWalletData && depositWalletData?.data.length > 0;

  const { data: kycStatus, refetch: refetchKycStatus } = useCheckStatus({ refetchOnMount: true });
  const isSellOnly =
    kycStatus?.status === KycStatus.SELL_ONLY || kycStatus?.status === KycStatus.SELL_ONLY_CONTACT_SUPPORT;

  const { handleSubmit, control, setValue, watch } = useForm<Partial<BuyCryptoStoreInterface>>({
    defaultValues: { wallet: '', manualEntryWallet: '', manualEntryWalletRepeat: '', memo: '' },
    mode: 'onTouched',
  });

  const manualWalletIcon = useMemo(() => {
    return coinsData.find((item) => {
      return item?.value === buyState?.get;
    })?.icon;
  }, [buyState?.get]);

  const watchedWalletType = watch('wallet');
  const watchedManualWallet = watch('manualEntryWallet');
  const debouncedWallet = useDebounce(watch('manualEntryWallet'));
  const watchWalletRepeat = watch('manualEntryWalletRepeat');
  const isERC20 = IsERC20Network(buyState.network) && isEVMNetwork(chains, buyState.network);
  const { data: selectedCoinNetworksData } = useGetCryptoNetworkData({
    cryptoCurrency: buyState.get || CryptoCurrency.ETH,
    enabled: true,
  });

  const selectedNetworkData = useMemo(
    () => selectedCoinNetworksData.filter((item) => item?.cryptoNetwork === buyState.network)[0],
    [buyState.network, selectedCoinNetworksData],
  );

  const onSubmit: SubmitHandler<Partial<BuyCryptoStoreInterface>> = (data) => {
    buyState.setData(data);
    if (
      (role === Roles.BUSINESS && kycStatus?.status !== KycStatus.BIOMETRIC_REQUIRED) ||
      kycStatus?.status === KycStatus.CLEAR
    ) {
      setStep(4);
    } else {
      state.open();
    }
    state.open();
  };

  const onMemoModalClose = () => {
    setIsMemoModalOpen(false);
  };

  const onMemoWarningClick = () => {
    setIsMemoModalOpen(true);
  };

  const { refetch } = useVerifyWallet([
    { crypto: buyState.network, address: debouncedWallet || '' },
    {
      enabled: false,
      onSuccess: (isVerified) => {
        if (watchedManualWallet?.length) {
          setWalletStatus(isVerified ? 'verified' : 'failed');

          const walletAddress = watch('manualEntryWallet');
          if (isVerified && walletAddress) {
            buyState.setWalletAddress(walletAddress);
          }
        }
      },
      onError: (e: any) => {
        if (watchedManualWallet?.length) {
          if (String(e?.response?.data?.message).includes(EXCEPTION_ERROR_MESSAGE)) {
            setWalletStatus('verified');
            const walletAddress = watch('manualEntryWallet');
            if (walletAddress) {
              buyState.setWalletAddress(walletAddress);
            }
          } else {
            setWalletStatus('failed');
          }
        }
      },
    },
  ]);

  async function validateWallet() {
    if (!debouncedWallet || !watchedManualWallet?.length) {
      return;
    }
    const walletRegexString = new RegExp(buyState?.manualEntryWalletRegex || '');

    const isValid = walletRegexString.test(debouncedWallet!);
    if (!isValid) {
      setWalletStatus('failedLocal');
      return;
    }
    setWalletStatus('verifying');
    refetch();
  }

  function onConnect(w?: OnConnectWallet) {
    let selectedWallet;
    WALLETS = WALLETS.map((el) => {
      const isSelectedWallet = w?.label.toUpperCase().includes(el.value.toString().toUpperCase());
      if (isSelectedWallet) {
        selectedWallet = {
          ...el,
          label: w?.label,
          value: w?.label ?? '',
          icon: w?.icon.startsWith('data') ? (
            <Image width={16} height={16} src={w?.icon ?? ''} alt="Wallet Icon" />
          ) : (
            <div dangerouslySetInnerHTML={{ __html: w?.icon ?? '' }}></div>
          ),
        };
        return selectedWallet;
      }
      return el;
    });

    if (!selectedWallet) {
      WALLETS = [
        {
          label: w?.label,
          value: w?.label ?? '',
          icon: w?.icon.startsWith('data') ? (
            <Image width={16} height={16} src={w?.icon ?? ''} alt="Wallet Icon" />
          ) : (
            <div dangerouslySetInnerHTML={{ __html: w?.icon ?? '' }}></div>
          ),
        },
        ...WALLETS,
      ];
    }

    const connectedW = WALLETS.find((wallet) => {
      if (typeof wallet.label === 'string') {
        return wallet.label.toLowerCase() === w?.label.toLowerCase();
      }
      return undefined;
    });
    const walletAddress = w?.accounts[0].address;

    if (connectedW && walletAddress) {
      setValue('wallet', connectedW.value as string);
      setConnectedWallet(connectedW);
      buyState.setWalletAddress(walletAddress);
      setWalletStatus('verified');
    }
  }

  const onSuccessVerificationHandler = async () => {
    if (role === Roles.PERSONAL) {
      refetchKycStatus().then((resp) => {
        if (resp.data?.status === KycStatus.SELL_ONLY) {
          showNotification('User is not allowed to buy crypto');
        } else {
          setStep(step + 1);
        }
      });
    } else {
      setStep(step + 1);
    }
  };

  const walletNetworkMessage = useMemo(() => {
    return renderWalletNetworkMessage(walletStatus, buyState.network);
  }, [buyState.network, walletStatus]);

  useEffect(() => {
    if (walletStatus !== null) {
      setWalletStatus(null);
    }

    setConnectedWallet(undefined);

    // @ts-ignore
    if (typeof window !== 'undefined' && window.ethereum) {
      const hasMetaMask = !!WALLETS.find((wallet) => wallet.label === 'MetaMask');
      if (!hasMetaMask) {
        WALLETS.unshift(METAMASK_WALLET_OBJECT);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validateWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedWallet, refetch]);

  useEffect(() => {
    if (watchedManualWallet?.length === 0) setWalletStatus(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedManualWallet]);

  useEffect(() => {
    if (!isERC20) {
      setValue('wallet', 'manualEntry');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isERC20]);

  useEffect(() => {
    refetchDepositWallets();
    connectWalletRef.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedNetworkData?.memoRegex) {
      setIsMemoModalOpen(true);
    }
  }, [selectedNetworkData?.memoRegex]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isERC20 && <ConnectWallet onConnect={onConnect} ref={connectWalletRef} isBuyFlow />}
      <div className={clsx(styles.formTitleWrapper, styles.walletTitleWrapper)}>
        <Button
          icon="arrowLeft"
          iconClassName={styles.iconBack}
          variants="subtle"
          size="smallSubtle"
          className={styles.buttonBack}
          onPress={() => {
            connectWalletRef.current?.disconnect();
            setStep(step - 1);
          }}
        />
        <h2 className={styles.formTitle}>Wallet Address</h2>
      </div>
      <p className={clsx(styles.formDescription, styles.walletDescription)}>
        Please, select wallet type from the list to connect it.
      </p>

      <Controller
        name="wallet"
        control={control}
        render={({ field }) => (
          <SelectRCWallet
            {...field}
            onSelectChange={(value) => {
              if (!isERC20) return;

              field.onChange(value?.value);
              if (value?.isManualEntry) {
                connectWalletRef.current?.disconnect();
                setValue('manualEntryWallet', '');
                setWalletStatus(null);
              } else {
                connectWalletRef.current?.connect(buyState.network);
              }
            }}
            wallets={!isERC20 ? [] : WALLETS}
            className={styles.selectWallet}
            label="Wallet Type"
            {...(watchedWalletType && watchedWalletType !== 'manualEntry' && { selectValue: connectedWallet })}
            {...((!isERC20 || watchedWalletType === 'manualEntry') && { selectValue: manualEntryOption })}
          />
        )}
      />
      {watchedWalletType === 'manualEntry' && (
        <div
          className={clsx(styles.inputWrapper, {
            [styles.inputWrapperWithError]: walletStatus === 'failed',
            [styles.inputWrapperWithLocalError]: walletStatus === 'failedLocal',
          })}
        >
          {hasDepositWallets ? (
            <Controller
              name="manualEntryWallet"
              control={control}
              render={({ field }) => (
                <Select
                  className={styles.manualInput}
                  label="Wallet Address"
                  placeholder="Enter Wallet Address"
                  allowsCustomValue
                  onInputChange={(e) => {
                    setValue('manualEntryWallet', e as unknown as string);
                  }}
                  onSelectionChange={(e) => {
                    setValue('manualEntryWallet', e as unknown as string);
                  }}
                >
                  {depositWalletData.data.map((item) => {
                    return (
                      <SelectListItem key={item?.address} textValue={item?.address ?? ''}>
                        {truncateString({
                          string: item?.address ?? '',
                        })}
                      </SelectListItem>
                    );
                  })}
                </Select>
              )}
            />
          ) : (
            <Controller
              name="manualEntryWallet"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  autoFocus={!isERC20}
                  size={isDesktop ? 'large' : 'medium'}
                  label="Wallet Address"
                  iconLeft={manualWalletIcon as IconType}
                  iconLeftClassName={styles.inputIcon}
                  placeholder="Enter Wallet Address"
                  className={styles.manualInput}
                  inputClassName={clsx({ [styles.manualInputField]: walletStatus === 'failed' })}
                />
              )}
            />
          )}
          {hasDepositWallets ? (
            <Controller
              name="manualEntryWalletRepeat"
              control={control}
              render={({ field }) => (
                <Select
                  className={clsx(styles.manualInput, styles.manualInputRepeat)}
                  label="Repeat Wallet Address"
                  placeholder="Enter Wallet Address Again"
                  allowsCustomValue
                  onInputChange={(e) => {
                    setValue('manualEntryWalletRepeat', e as unknown as string);
                  }}
                  onSelectionChange={(e) => {
                    setValue('manualEntryWalletRepeat', e as unknown as string);
                  }}
                >
                  {depositWalletData.data.map((item) => {
                    return (
                      <SelectListItem key={item?.address} textValue={item?.address ?? ''}>
                        {truncateString({
                          string: item?.address ?? '',
                        })}
                      </SelectListItem>
                    );
                  })}
                </Select>
              )}
            />
          ) : (
            <Controller
              name="manualEntryWalletRepeat"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  size={isDesktop ? 'large' : 'medium'}
                  label="Repeat Wallet Address"
                  placeholder="Enter Wallet Address Again"
                  className={clsx(styles.manualInput, styles.manualInputRepeat)}
                  inputClassName={clsx({ [styles.manualInputField]: walletStatus === 'failed' })}
                />
              )}
            />
          )}

          {walletNetworkMessage}

          {renderInputLabel(walletStatus, isDesktop, hasDepositWallets)}
        </div>
      )}

      {selectedNetworkData?.memoRegex && (
        <>
          <div className={clsx(styles.memoInputWrapper)}>
            <Controller
              name="memo"
              control={control}
              render={({ field: { ref, value, onChange, ...rest } }) => {
                return (
                  <Input
                    onChange={(text) => {
                      const regex = new RegExp(String(selectedNetworkData?.memoRegex));
                      if (text.length > 0 && !regex.test(text)) {
                        setMemoFieldError('Incorrect Destination Tag/Memo format');
                      }
                      if (regex.test(text) || text.length === 0) {
                        setMemoFieldError('');
                      }
                      onChange(allowOnlyNumber(text));
                    }}
                    value={value}
                    size={isDesktop ? 'large' : 'medium'}
                    label="DESTINATION TAG/MEMO"
                    placeholder="123456789"
                    labelClassName={styles.label}
                    errorMessage={memoFieldError ?? ''}
                  />
                );
              }}
            />
          </div>

          <div className={styles.memoWarningWrapper}>
            <div onClick={onMemoWarningClick}>
              <Icon name="infoCircled" className={styles.memoWarningIcon} />
            </div>
            <span className={styles.memoWarningText}>
              {buyState?.get} transactions with incorrect or missing MEMO for your receiving address will be lost.
            </span>
          </div>
        </>
      )}

      {walletStatus !== 'failed' && (
        <MonitoredBy className={styles.walletMonitoredBy} descriptionClassName={styles.walletMonitoredByDescription} />
      )}

      <OverlayProvider className={styles.popupTrigger}>
        <Button
          isDisabled={walletStatus !== 'verified' || watchedManualWallet !== watchWalletRepeat || !!memoFieldError}
          size={isDesktop ? 'large' : 'medium'}
          type="submit"
        >
          Continue
        </Button>
        <Popup
          isOpen={state.isOpen}
          onClose={state.close}
          hasCloseIcon
          isDismissable={false}
          dialogClassName={styles.popup}
        >
          {!isSellOnly ? <Verification onSuccessVerification={onSuccessVerificationHandler} /> : <NotAllowedWarning />}
        </Popup>

        <Popup isOpen={isMemoModalOpen} dialogClassName={styles.memoModal}>
          <MemoWarning coinName={buyState?.get} isBuyFlowWarning={true} onExitClick={onMemoModalClose} />
        </Popup>
      </OverlayProvider>
    </form>
  );
};
